import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileText, Facebook, Instagram, Image as ImageIcon, Mail, Youtube,
  Linkedin, AtSign, Twitter, Music2, Newspaper,
  Plus, Trash2, CalendarRange, Compass, Sparkles, Maximize2, Loader2, Copy, Save, History,
  ClipboardCheck, CheckCircle2, X, Star,
} from "lucide-react";
import { DEFAULT_PLATFORM_KEYS } from "@/lib/constants";

const FORMAT_CATALOG = {
  blog: { label: "Blog", icon: FileText },
  facebook: { label: "Facebook", icon: Facebook },
  instagram: { label: "Instagram", icon: Instagram },
  linkedin: { label: "LinkedIn", icon: Linkedin },
  pinterest: { label: "Pinterest", icon: ImageIcon },
  email: { label: "Email", icon: Mail },
  youtube: { label: "YouTube", icon: Youtube },
  threads: { label: "Threads", icon: AtSign },
  x: { label: "X (Twitter)", icon: Twitter },
  tiktok: { label: "TikTok", icon: Music2 },
  newsletter: { label: "Newsletter", icon: Newspaper },
};
const buildFormats = (keys) => (keys || []).filter((k) => FORMAT_CATALOG[k]).map((k) => ({ key: k, ...FORMAT_CATALOG[k] }));
const DEFAULT_FORMATS = buildFormats(DEFAULT_PLATFORM_KEYS);

const STAGES = ["Idea", "Draft", "Scheduled", "Completed"];
const STAGE_TONE = {
  Idea: "bg-muted text-muted-foreground",
  Draft: "bg-accent/20 text-secondary",
  Scheduled: "bg-secondary/15 text-secondary",
  Completed: "bg-primary text-primary-foreground",
};

const OPENING_STYLES = [
  "bold statement", "reflective observation", "personal story", "data point or fact",
  "direct question", "practical insight", "surprising truth", "clear promise",
  "memorable line",
];

const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const firstLine = (t) => (t || "").trim().split("\n").find((l) => l.trim()) || "";

function thisMonday() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

function fmtWeek(iso) {
  if (!iso) return "Untitled week";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return "Week of " + d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function mergeFormats(w, list) {
  const merged = {};
  (list || DEFAULT_FORMATS).forEach((f) => {
    merged[f.key] = { content: "", stage: "Idea", ...(w?.formats?.[f.key] || {}) };
  });
  return merged;
}

export default function ContentCommandCenter() {
  const [weeks, setWeeks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [newDate, setNewDate] = useState(thisMonday());
  const [deleting, setDeleting] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(null);
  const [draftingAll, setDraftingAll] = useState(false);
  const [editorKey, setEditorKey] = useState(null);
  const [reuseOpen, setReuseOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewSummary, setReviewSummary] = useState("");
  const [reviewSuggestions, setReviewSuggestions] = useState([]);
  const [previewSug, setPreviewSug] = useState(null);
  const [FORMATS, setFORMATS] = useState(DEFAULT_FORMATS);
  const [savedThemes, setSavedThemes] = useState([]);
  const [themeSuggestions, setThemeSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  const loadThemes = useCallback(async () => {
    try { const r = await api.get("/content-themes"); setSavedThemes(r.data || []); }
    catch (e) { /* non-fatal */ }
  }, []);

  const load = useCallback(async () => {
    try {
      let fmts = DEFAULT_FORMATS;
      try {
        const s = await api.get("/business-setup");
        const keys = (s.data?.primary_platforms || []).filter(Boolean);
        const built = buildFormats(keys);
        if (built.length) fmts = built;
      } catch (e) { /* fall back to default formats */ }
      setFORMATS(fmts);
      const r = await api.get("/weeks");
      setWeeks(r.data);
      if (r.data.length) {
        const first = r.data[0];
        setSelectedId(first.id);
        setWeek({ ...first, formats: mergeFormats(first, fmts) });
      } else {
        setSelectedId(null);
        setWeek(null);
      }
    } catch (e) { toast.error("Could not load your content weeks."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadThemes(); }, [loadThemes]);

  const saveTheme = async (name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    if (savedThemes.some((t) => t.name.toLowerCase() === clean.toLowerCase())) { toast.message("Already saved."); return; }
    try {
      const r = await api.post("/content-themes", { name: clean });
      setSavedThemes((prev) => [r.data, ...prev.filter((t) => t.id !== r.data.id)]);
      setThemeSuggestions((prev) => prev.filter((t) => t.toLowerCase() !== clean.toLowerCase()));
      toast.success(`Saved "${clean}" to your themes.`);
    } catch (e) { toast.error("Could not save that theme."); }
  };

  const removeSavedTheme = async (id) => {
    try { await api.delete(`/content-themes/${id}`); setSavedThemes((prev) => prev.filter((t) => t.id !== id)); toast.success("Theme removed."); }
    catch (e) { toast.error("Could not remove that theme."); }
  };

  const suggestThemes = async () => {
    setSuggesting(true);
    try {
      const r = await api.post("/content-themes/suggestions");
      const list = r.data?.suggestions || [];
      setThemeSuggestions(list);
      if (list.length === 0) toast.message("No new suggestions right now — try adding more to your Business Setup or Brand Center.");
      else toast.success("Fresh theme ideas for your business.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate suggestions right now."); }
    finally { setSuggesting(false); }
  };

  const selectWeek = (id) => {
    const w = weeks.find((x) => x.id === id);
    if (w) { setSelectedId(id); setWeek({ ...w, formats: mergeFormats(w, FORMATS) }); setReviewSummary(""); setReviewSuggestions([]); }
  };

  const createWeek = async () => {
    try {
      const r = await api.post("/weeks", { week_start: newDate, core_theme: "", notes: "", formats: {} });
      toast.success("New week created.");
      setNewOpen(false);
      const created = { ...r.data, formats: mergeFormats(r.data, FORMATS) };
      setWeeks((prev) => [created, ...prev.filter((w) => w.id !== created.id)].sort((a, b) => (a.week_start < b.week_start ? 1 : -1)));
      setSelectedId(created.id);
      setWeek(created);
    } catch (e) { toast.error("Could not create the week."); }
  };

  const persist = async (updated) => {
    try {
      const payload = {
        week_start: updated.week_start || "",
        core_theme: updated.core_theme || "",
        theme: updated.theme || "",
        notes: updated.notes || "",
        formats: updated.formats || {},
      };
      await api.put(`/weeks/${updated.id}`, payload);
      setWeeks((prev) => prev.map((w) => (w.id === updated.id ? { ...w, ...payload } : w)));
      return true;
    } catch (e) { toast.error("Could not save. Please try again."); return false; }
  };

  const setField = (key, value) => setWeek((w) => ({ ...w, [key]: value }));

  // Theme entered once: setting the theme also fills the core theme,
  // unless the founder has already typed a distinct core theme.
  const applyTheme = (value) => {
    setWeek((w) => {
      const core = (w.core_theme || "").trim();
      const prevTheme = (w.theme || "").trim();
      const syncCore = !core || core === prevTheme;
      const updated = { ...w, theme: value, ...(syncCore ? { core_theme: value } : {}) };
      persist(updated);
      return updated;
    });
  };

  const setStage = (fmtKey, stage) => {
    setWeek((w) => {
      const updated = { ...w, formats: { ...w.formats, [fmtKey]: { ...w.formats[fmtKey], stage } } };
      persist(updated);
      return updated;
    });
  };

  const setContent = (fmtKey, content) => {
    setWeek((w) => ({ ...w, formats: { ...w.formats, [fmtKey]: { ...w.formats[fmtKey], content } } }));
  };

  const otherOpenings = (fmtKey) =>
    FORMATS.filter((f) => f.key !== fmtKey)
      .map((f) => firstLine(week?.formats?.[f.key]?.content).slice(0, 160))
      .filter(Boolean);

  const styleFor = (fmtKey) => {
    const idx = FORMATS.findIndex((f) => f.key === fmtKey);
    const seed = hashStr((week?.week_start || "") + "|" + (week?.core_theme || ""));
    return OPENING_STYLES[(seed + idx) % OPENING_STYLES.length];
  };

  const recentOpenings = (excludeId, theme) => {
    const lines = [];
    const pool = weeks.filter((w) => w.id !== excludeId);
    const sameTheme = theme ? pool.filter((w) => (w.theme || "") === theme).slice(0, 6) : [];
    const recent = pool.slice(0, 4);
    [...sameTheme, ...recent].forEach((w) => {
      FORMATS.forEach((f) => {
        const l = firstLine(w?.formats?.[f.key]?.content).slice(0, 160);
        if (l) lines.push(l);
      });
    });
    return Array.from(new Set(lines)).slice(0, 24);
  };

  const generate = async (fmtKey, mode, opts = {}) => {
    const { silent = false, style, avoidOpenings } = opts;
    if (mode === "draft" && !(week.core_theme || "").trim()) {
      toast.error("Add a core theme first, so the writing has something to hold onto.");
      return "";
    }
    const existing = week.formats[fmtKey]?.content || "";
    if (mode === "improve" && !existing.trim()) {
      toast.error("Nothing to improve yet. Draft from the theme first.");
      return "";
    }
    setGeneratingKey(fmtKey);
    try {
      const label = FORMATS.find((f) => f.key === fmtKey)?.label || fmtKey;
      const chosenStyle = style || OPENING_STYLES[Math.floor(Math.random() * OPENING_STYLES.length)];
      const avoidBase = avoidOpenings || Array.from(new Set([...otherOpenings(fmtKey), ...recentOpenings(week?.id, week?.theme)]));
      const avoid = avoidBase.slice(0, 24);
      const r = await api.post("/ai/generate-format", {
        format: fmtKey, format_label: label, mode,
        core_theme: week.core_theme || "", theme: week.theme || "", notes: week.notes || "", existing_content: existing,
        opening_style: chosenStyle, avoid_openings: avoid,
      });
      const text = r.data.text || "";
      setWeek((w) => {
        const updated = { ...w, formats: { ...w.formats, [fmtKey]: { ...w.formats[fmtKey], content: text } } };
        persist(updated);
        return updated;
      });
      toast[silent ? "message" : "success"](
        mode === "improve" ? "Gently polished. It's yours to adjust." : `${label} ready.`
      );
      return text;
    } catch (e) {
      toast.error("The writing helper is unavailable right now. Please try again in a moment.");
      return "";
    } finally {
      setGeneratingKey(null);
    }
  };

  const openEditor = (key, auto = false) => {
    setEditorKey(key);
    if (auto && !(week.formats[key]?.content || "").trim()) {
      generate(key, "draft");
    }
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success("Copied to clipboard."); }
    catch (e) { toast.error("Could not copy."); }
  };

  const draftAll = async () => {
    if (!(week.core_theme || "").trim()) {
      toast.error("Add a core theme first, so every format has the same message to hold onto.");
      return;
    }
    const targets = FORMATS.filter((f) => !(week.formats[f.key]?.content || "").trim());
    if (targets.length === 0) {
      toast.message("Every format already has a draft. Open a card to regenerate or refine it.");
      return;
    }
    setDraftingAll(true);
    const avoidAcc = Array.from(new Set([
      ...FORMATS.map((f) => firstLine(week.formats[f.key]?.content).slice(0, 160)).filter(Boolean),
      ...recentOpenings(week.id, week.theme),
    ]));
    try {
      const failed = [];
      let succeeded = 0;
      for (const f of targets) {
        const text = await generate(f.key, "draft", { silent: true, style: styleFor(f.key), avoidOpenings: [...avoidAcc] });
        if ((text || "").trim()) {
          succeeded += 1;
          const opening = firstLine(text).slice(0, 160);
          if (opening) avoidAcc.push(opening);
        } else {
          failed.push(f.label);
        }
      }
      if (failed.length === 0) {
        toast.success(`Drafted ${succeeded} format${succeeded > 1 ? "s" : ""} from this week's theme.`);
      } else if (succeeded > 0) {
        toast.warning(`Drafted ${succeeded} of ${targets.length}. Couldn't generate: ${failed.join(", ")}. Open those cards to try again.`);
      } else {
        toast.error(`Couldn't generate any formats just now (${failed.join(", ")}). Please try again shortly.`);
      }
    } finally {
      setDraftingAll(false);
    }
  };

  const reviewWeek = async () => {
    const pieces = FORMATS
      .map((f) => ({ key: f.key, format_label: f.label, content: (week.formats[f.key]?.content || "").trim() }))
      .filter((p) => p.content);
    if (pieces.length < 2) {
      toast.error("Draft the formats first, then I can review the whole week for repetition.");
      return;
    }
    setReviewing(true);
    setReviewSummary("");
    setReviewSuggestions([]);
    try {
      const r = await api.post("/ai/review-week", {
        theme: week.theme || "", core_theme: week.core_theme || "", pieces,
      });
      const suggestions = r.data.suggestions || [];
      setReviewSummary(r.data.summary || "Reviewed. Nothing stood out as repetitive.");
      setReviewSuggestions(suggestions);
      toast.success(
        suggestions.length
          ? `${suggestions.length} suggestion${suggestions.length > 1 ? "s" : ""}. Review and choose what to keep.`
          : "Reviewed. Your formats already read distinctly."
      );
    } catch (e) {
      toast.error("The editorial review is unavailable right now. Please try again in a moment.");
    } finally {
      setReviewing(false);
    }
  };

  // Apply a single suggestion ONLY when the founder approves it. Persist first, then update the UI
  // so a failed save can never leave unsaved strengthened text on screen.
  const applySuggestion = async (sug) => {
    const updated = {
      ...week,
      formats: { ...week.formats, [sug.key]: { ...week.formats[sug.key], content: sug.suggestion } },
    };
    const ok = await persist(updated);
    if (ok) {
      setWeek(updated);
      setWeeks((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));
      setReviewSuggestions((prev) => prev.filter((s) => s.key !== sug.key));
      setPreviewSug(null);
      toast.success(`${sug.format_label} strengthened and saved.`);
    }
  };

  const keepMine = (sug) => {
    setReviewSuggestions((prev) => prev.filter((s) => s.key !== sug.key));
    setPreviewSug(null);
    toast.message(`Kept your ${sug.format_label} as is.`);
  };

  const dismissReview = () => { setReviewSummary(""); setReviewSuggestions([]); };

  const usedThemes = Array.from(
    new Set(weeks.map((w) => (w.theme || "").trim() || (w.core_theme || "").trim()).filter(Boolean))
  );

  // Next Monday after the latest existing week (never an old/fixed date).
  const nextWeekStart = () => {
    if (!weeks.length) return thisMonday();
    const latest = weeks.reduce((m, w) => (w.week_start > m ? w.week_start : m), weeks[0].week_start);
    const d = new Date(latest + "T00:00:00");
    if (isNaN(d)) return thisMonday();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  };

  const reuseTheme = async (themeValue) => {
    setReuseOpen(false);
    let created;
    try {
      const r = await api.post("/weeks", { week_start: newDate, core_theme: themeValue, theme: themeValue, notes: "", formats: {} });
      created = { ...r.data, formats: mergeFormats(r.data, FORMATS) };
      setWeeks((prev) => [created, ...prev.filter((w) => w.id !== created.id)].sort((a, b) => (a.week_start < b.week_start ? 1 : -1)));
      setSelectedId(created.id);
      setWeek(created);
    } catch (e) { toast.error("Could not start a fresh week from that theme."); return; }

    setDraftingAll(true);
    let current = created;
    const avoidAcc = Array.from(new Set(recentOpenings(created.id, themeValue)));

    // Lightweight freshness: summarize prior same-theme content so the AI avoids repeating it.
    let priorSummary = "";
    const priorPieces = [];
    weeks
      .filter((w) => w.id !== created.id && ((w.theme || "").trim() === themeValue || (w.core_theme || "").trim() === themeValue))
      .slice(0, 6)
      .forEach((w) => FORMATS.forEach((f) => {
        const c = w?.formats?.[f.key]?.content;
        if (c && c.trim()) priorPieces.push({ format_label: f.label, content: c });
      }));
    if (priorPieces.length) {
      try {
        const s = await api.post("/ai/summarize-theme", { theme: themeValue, pieces: priorPieces.slice(0, 14) });
        priorSummary = s.data.summary || "";
      } catch (e) { /* non-fatal — reuse still proceeds */ }
    }

    try {
      for (let i = 0; i < FORMATS.length; i++) {
        const f = FORMATS[i];
        setGeneratingKey(f.key);
        const style = OPENING_STYLES[(hashStr((created.week_start || "") + "|" + themeValue) + i) % OPENING_STYLES.length];
        const resp = await api.post("/ai/generate-format", {
          format: f.key, format_label: f.label, mode: "draft",
          core_theme: themeValue, theme: themeValue, notes: "", existing_content: "",
          opening_style: style, avoid_openings: avoidAcc.slice(0, 24), reuse: true, prior_summary: priorSummary,
        });
        const text = resp.data.text || "";
        current = { ...current, formats: { ...current.formats, [f.key]: { ...current.formats[f.key], content: text } } };
        setWeek(current);
        const opening = firstLine(text).slice(0, 160);
        if (opening) avoidAcc.push(opening);
      }
      await persist(current);
      setWeeks((prev) => prev.map((w) => (w.id === current.id ? current : w)));
      toast.success(`Fresh week from "${themeValue}". New angles drafted.`);
    } catch (e) {
      toast.error("The writing helper is unavailable. The week was created. Try Draft all in a moment.");
    } finally {
      setDraftingAll(false);
      setGeneratingKey(null);
    }
  };

  const removeWeek = async () => {
    try {
      await api.delete(`/weeks/${selectedId}`);
      toast.success("Week removed.");
      setDeleting(false);
      const remaining = weeks.filter((w) => w.id !== selectedId);
      setWeeks(remaining);
      if (remaining.length) { setSelectedId(remaining[0].id); setWeek({ ...remaining[0], formats: mergeFormats(remaining[0], FORMATS) }); }
      else { setSelectedId(null); setWeek(null); }
    } catch (e) { toast.error("Could not remove the week."); }
  };

  const completedCount = week ? FORMATS.filter((f) => week.formats[f.key]?.stage === "Completed" && (week.formats[f.key]?.content || "").trim()).length : 0;
  const pct = Math.round((completedCount / FORMATS.length) * 100);

  return (
    <div>
      <PageHeader
        testid="content-header"
        title="Content Command Center"
        subtitle="One core message each week, repurposed across your chosen platforms."
        action={
          <div className="flex items-center gap-2">
            {usedThemes.length > 0 && (
              <Button variant="outline" onClick={() => { setNewDate(nextWeekStart()); setReuseOpen(true); }} data-testid="reuse-theme-button" className="rounded-full border-border gap-2">
                <History className="h-4 w-4" /> Reuse theme
              </Button>
            )}
            <Button onClick={() => { setNewDate(nextWeekStart()); setNewOpen(true); }} data-testid="new-week-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
              <Plus className="h-4 w-4" /> New week
            </Button>
          </div>
        }
      />

      {loading ? null : !week ? (
        <Card className="rounded-3xl border-border shadow-soft p-12 text-center" data-testid="content-empty">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
            <CalendarRange className="h-7 w-7 text-primary" />
          </div>
          <h2 className="font-heading text-2xl mb-2">Begin this week</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">Set one core theme, then shape it into content for each of your platforms, at your own pace.</p>
          <Button onClick={() => { setNewDate(nextWeekStart()); setNewOpen(true); }} data-testid="start-week-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
            <Plus className="h-4 w-4" /> Start this week
          </Button>
        </Card>
      ) : (
        <>
          {/* Week selector + core theme */}
          <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6" data-testid="week-panel">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
              <Select value={selectedId} onValueChange={selectWeek}>
                <SelectTrigger className="rounded-full w-full sm:w-64 bg-white border-border" data-testid="week-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {weeks.map((w) => <SelectItem key={w.id} value={w.id}>{fmtWeek(w.week_start)}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex-1" />
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground" data-testid="week-progress-text">{completedCount} of {FORMATS.length} completed</span>
                <button onClick={() => setDeleting(true)} data-testid="delete-week-button" className="p-2 rounded-full text-muted-foreground hover:text-destructive transition-colors" title="Remove week">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <Progress value={pct} className="h-2 mb-6" data-testid="week-progress-bar" />

            <div className="space-y-3 mb-5" data-testid="theme-library">
              <div className="flex items-center justify-between">
                <Label>Theme library</Label>
                <Button variant="outline" size="sm" onClick={suggestThemes} disabled={suggesting} data-testid="suggest-themes-button" className="rounded-full border-border gap-1.5 h-8">
                  {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {suggesting ? "Thinking…" : "Suggest more with AI"}
                </Button>
              </div>

              {savedThemes.length > 0 && (
                <div data-testid="saved-themes">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Your saved themes</p>
                  <div className="flex flex-wrap gap-2">
                    {savedThemes.map((t) => (
                      <span key={t.id} data-testid={`saved-theme-${t.id}`}
                        className={`group inline-flex items-center gap-1 text-xs pl-3 pr-1.5 py-1.5 rounded-full transition-colors ${week.theme === t.name ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
                        <button onClick={() => applyTheme(t.name)} data-testid={`apply-saved-theme-${t.id}`}>{t.name}</button>
                        <button onClick={() => removeSavedTheme(t.id)} data-testid={`remove-saved-theme-${t.id}`} className="opacity-50 hover:opacity-100 transition-opacity" title="Remove saved theme"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                const savedLower = new Set(savedThemes.map((s) => s.name.toLowerCase()));
                const pastThemes = usedThemes.filter((t) => !savedLower.has(t.toLowerCase()));
                if (pastThemes.length === 0) return null;
                return (
                  <div data-testid="past-themes">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">From your past weeks</p>
                    <div className="flex flex-wrap gap-2">
                      {pastThemes.map((t) => (
                        <span key={t} data-testid={`past-theme-${t.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`group inline-flex items-center gap-1 text-xs pl-3 pr-1.5 py-1.5 rounded-full transition-colors ${week.theme === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
                          <button onClick={() => applyTheme(t)}>{t}</button>
                          <button onClick={() => saveTheme(t)} title="Save as recurring theme" className="opacity-50 hover:opacity-100 transition-opacity"><Star className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {themeSuggestions.length > 0 && (
                <div data-testid="theme-suggestions">
                  <p className="text-[11px] uppercase tracking-wide text-secondary mb-1.5">Suggested for your business</p>
                  <div className="flex flex-wrap gap-2">
                    {themeSuggestions.map((t) => (
                      <span key={t} data-testid={`suggested-theme-${t.toLowerCase().replace(/\s+/g, "-")}`}
                        className="group inline-flex items-center gap-1 text-xs pl-3 pr-1.5 py-1.5 rounded-full bg-accent/20 text-secondary hover:bg-accent/30 transition-colors">
                        <button onClick={() => applyTheme(t)} data-testid={`apply-suggested-theme-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</button>
                        <button onClick={() => saveTheme(t)} title="Save as recurring theme" className="opacity-50 hover:opacity-100 transition-opacity"><Star className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {savedThemes.length === 0 && usedThemes.length === 0 && themeSuggestions.length === 0 && (
                <p className="text-xs text-muted-foreground">No themes yet. Tap <span className="text-secondary">Suggest more with AI</span> for ideas tailored to your business, or type your own below.</p>
              )}

              <Input
                data-testid="theme-input"
                value={week.theme || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setWeek((w) => {
                    const core = (w.core_theme || "").trim();
                    const prevTheme = (w.theme || "").trim();
                    const syncCore = !core || core === prevTheme;
                    return { ...w, theme: value, ...(syncCore ? { core_theme: value } : {}) };
                  });
                }}
                onBlur={() => persist(week)}
                placeholder="or type your own theme…"
                className="rounded-xl mt-1"
              />
            </div>
            <div className="space-y-2 mb-5">
              <Label className="flex items-center gap-2"><Compass className="h-4 w-4 text-accent" /> Core theme this week</Label>
              <Input
                data-testid="core-theme-input"
                value={week.core_theme || ""}
                onChange={(e) => setField("core_theme", e.target.value)}
                onBlur={() => persist(week)}
                placeholder="What is the one message this week is about?"
                className="rounded-xl text-base"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea
                data-testid="week-notes-input"
                value={week.notes || ""}
                onChange={(e) => setField("notes", e.target.value)}
                onBlur={() => persist(week)}
                placeholder="Angle, key points, or anything to hold onto…"
                className="rounded-xl min-h-[70px]"
              />
            </div>
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <Button onClick={draftAll} disabled={draftingAll} data-testid="draft-all-button"
                className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-2">
                {draftingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {draftingAll ? "Writing all…" : "Draft all with AI"}
              </Button>
              <Button onClick={reviewWeek} disabled={reviewing || draftingAll} variant="outline" data-testid="review-week-button"
                className="rounded-full border-border gap-2">
                {reviewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                {reviewing ? "Reviewing the week…" : "Review & polish the week"}
              </Button>
              <span className="text-xs text-muted-foreground">Draft fills every empty format; Review checks them all for overlap and suggests changes. Nothing is saved until you approve it.</span>
            </div>
          </Card>

          {/* Editorial review — advisory; never overwrites without approval */}
          {(reviewSummary || reviewSuggestions.length > 0) && (
            <Card className="rounded-3xl border-primary/20 bg-primary/5 shadow-soft p-5 sm:p-6 mb-6" data-testid="review-summary-panel">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-lg mb-1">Weekly editorial review</h3>
                  <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed" data-testid="review-summary-text">{reviewSummary}</p>
                </div>
                <button onClick={dismissReview} data-testid="dismiss-review-summary" className="p-1.5 rounded-full text-muted-foreground hover:text-foreground transition-colors" title="Dismiss">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {reviewSuggestions.length > 0 && (
                <div className="mt-5 space-y-3" data-testid="review-suggestions">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Suggested changes, you decide</p>
                  {reviewSuggestions.map((sug) => {
                    const f = FORMATS.find((x) => x.key === sug.key);
                    const Icon = f?.icon || FileText;
                    return (
                      <div key={sug.key} className="rounded-2xl bg-white border border-border p-4" data-testid={`review-suggestion-${sug.key}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <Icon className="h-4 w-4 text-primary" strokeWidth={1.75} />
                          <span className="font-heading text-base">{sug.format_label}</span>
                        </div>
                        <p className="text-sm text-foreground/75 leading-relaxed mb-3" data-testid={`review-note-${sug.key}`}>{sug.note}</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button size="sm" onClick={() => setPreviewSug(sug)} data-testid={`review-compare-${sug.key}`} variant="ghost" className="rounded-full text-secondary gap-1.5">
                            <Maximize2 className="h-3.5 w-3.5" /> Compare
                          </Button>
                          <div className="flex-1" />
                          <Button size="sm" variant="outline" onClick={() => keepMine(sug)} data-testid={`review-keep-${sug.key}`} className="rounded-full border-border">
                            Keep mine
                          </Button>
                          <Button size="sm" onClick={() => applySuggestion(sug)} data-testid={`review-apply-${sug.key}`} className="rounded-full bg-primary hover:bg-primary/90 gap-1.5">
                            <Sparkles className="h-3.5 w-3.5" /> Use strengthened version
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {/* Format board */}
          <div className="grid md:grid-cols-2 gap-5" data-testid="format-board">
            {FORMATS.map((f) => {
              const Icon = f.icon;
              const fmt = week.formats[f.key] || { content: "", stage: "Idea" };
              return (
                <Card key={f.key} className="rounded-3xl border-border shadow-soft p-6" data-testid={`format-card-${f.key}`}>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                      </div>
                      <h3 className="font-heading text-lg">{f.label}</h3>
                    </div>
                    <Select value={fmt.stage} onValueChange={(v) => setStage(f.key, v)}>
                      <SelectTrigger className={`rounded-full w-32 border-0 text-xs h-8 ${STAGE_TONE[fmt.stage]}`} data-testid={`stage-select-${f.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative">
                    <Textarea
                      data-testid={`format-content-${f.key}`}
                      value={fmt.content || ""}
                      onChange={(e) => setContent(f.key, e.target.value)}
                      onBlur={() => persist(week)}
                      placeholder={`Draft the ${f.label} version of this week's message…`}
                      className="rounded-xl min-h-[130px] bg-muted/30 border-border"
                    />
                    {generatingKey === f.key && (
                      <div className="absolute inset-0 rounded-xl bg-white/70 backdrop-blur-sm flex items-center justify-center gap-2 text-sm text-primary">
                        <Loader2 className="h-4 w-4 animate-spin" /> Writing gently…
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" onClick={() => openEditor(f.key, true)} data-testid={`ai-draft-${f.key}`}
                      className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" /> AI Draft
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditor(f.key, false)} data-testid={`open-editor-${f.key}`}
                      className="rounded-full text-muted-foreground gap-1.5">
                      <Maximize2 className="h-3.5 w-3.5" /> Open
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Compare suggestion (current vs strengthened) — approve or decline */}
      <Dialog open={!!previewSug} onOpenChange={(o) => { if (!o) setPreviewSug(null); }}>
        <DialogContent className="rounded-3xl max-w-3xl" data-testid="review-compare-dialog">
          {previewSug && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Compare: {previewSug.format_label}</DialogTitle>
              </DialogHeader>
              <DialogDescription className="text-sm text-foreground/70 leading-relaxed">{previewSug.note}</DialogDescription>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Your current version</p>
                  <div className="rounded-xl bg-muted/30 border border-border p-3 text-sm whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto" data-testid="compare-current">
                    {week?.formats?.[previewSug.key]?.content || "—"}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-secondary mb-1.5">Strengthened version</p>
                  <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-sm whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto" data-testid="compare-suggested">
                    {previewSug.suggestion}
                  </div>
                </div>
              </div>
              <DialogFooter className="flex-row justify-end gap-2">
                <Button variant="outline" onClick={() => keepMine(previewSug)} data-testid="compare-keep" className="rounded-full border-border">Keep mine</Button>
                <Button onClick={() => applySuggestion(previewSug)} data-testid="compare-apply" className="rounded-full bg-primary hover:bg-primary/90 gap-1.5">
                  <Sparkles className="h-4 w-4" /> Use strengthened version
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Focused format editor */}
      <Dialog open={!!editorKey} onOpenChange={(o) => { if (!o) { persist(week); setEditorKey(null); } }}>
        <DialogContent className="rounded-3xl max-w-2xl" data-testid="format-editor-dialog">
          {editorKey && week && (() => {
            const f = FORMATS.find((x) => x.key === editorKey);
            const Icon = f.icon;
            const fmt = week.formats[editorKey] || { content: "", stage: "Idea" };
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" /> {f.label}
                  </DialogTitle>
                </DialogHeader>
                <DialogDescription className="text-xs text-muted-foreground -mt-1 leading-relaxed">
                  Drawing on your Brand Center voice, audience &amp; content standards · <span className="text-secondary">Theme:</span> {week.core_theme || "—"}
                </DialogDescription>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" disabled={generatingKey === editorKey} onClick={() => generate(editorKey, "draft")} data-testid="editor-ai-draft" className="rounded-full border-border gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> AI Draft
                  </Button>
                  <Button variant="outline" size="sm" disabled={generatingKey === editorKey} onClick={() => generate(editorKey, "improve")} data-testid="editor-ai-improve" className="rounded-full border-border gap-1.5">
                    <Compass className="h-3.5 w-3.5" /> Improve
                  </Button>
                  <div className="flex-1" />
                  <span className="text-xs text-muted-foreground">Status</span>
                  {["Draft", "Completed"].map((s) => (
                    <button key={s} onClick={() => setStage(editorKey, s)} data-testid={`editor-status-${s.toLowerCase()}`}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${fmt.stage === s ? STAGE_TONE[s] : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
                      {s}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Textarea
                    data-testid="editor-content"
                    value={fmt.content || ""}
                    onChange={(e) => setContent(editorKey, e.target.value)}
                    onBlur={() => persist(week)}
                    placeholder={`Write the ${f.label} version here…`}
                    className="rounded-xl min-h-[320px] bg-muted/20 border-border leading-relaxed"
                  />
                  {generatingKey === editorKey && (
                    <div className="absolute inset-0 rounded-xl bg-white/70 backdrop-blur-sm flex items-center justify-center gap-2 text-sm text-primary">
                      <Loader2 className="h-4 w-4 animate-spin" /> Writing gently…
                    </div>
                  )}
                </div>
                <DialogFooter className="flex-row justify-end gap-2">
                  <Button variant="outline" onClick={() => copyText(fmt.content)} data-testid="editor-copy" className="rounded-full border-border gap-1.5">
                    <Copy className="h-4 w-4" /> Copy
                  </Button>
                  <Button variant="outline" onClick={async () => { const ok = await persist(week); if (ok) toast.success("Saved."); }} data-testid="editor-save" className="rounded-full border-border gap-1.5">
                    <Save className="h-4 w-4" /> Save
                  </Button>
                  <Button onClick={() => { persist(week); setEditorKey(null); }} data-testid="editor-done" className="rounded-full bg-primary hover:bg-primary/90">Done</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Reuse theme dialog */}
      <Dialog open={reuseOpen} onOpenChange={setReuseOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="reuse-theme-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Reuse a theme</DialogTitle></DialogHeader>
          <DialogDescription>Start a fresh week from a theme you have written before, with new angles, openings and examples, avoiding recent repetition.</DialogDescription>
          <div className="flex flex-wrap gap-2 py-2 max-h-72 overflow-y-auto">
            {usedThemes.map((t) => (
              <button key={t} data-testid={`reuse-theme-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => reuseTheme(t)}
                className="text-sm px-4 py-2 rounded-full bg-muted text-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                {t}
              </button>
            ))}
          </div>
          <p data-testid="reuse-theme-description" className="text-xs text-muted-foreground">A new week (starting {fmtWeek(newDate)}) will be created and all your formats drafted fresh.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReuseOpen(false)} data-testid="reuse-theme-cancel-button" className="rounded-full">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New week dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-3xl sm:max-w-sm" data-testid="new-week-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New content week</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Choose the Monday this content week begins.</DialogDescription>
          <div className="space-y-2 py-2">
            <Label>Week starting</Label>
            <Input type="date" data-testid="new-week-date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)} data-testid="new-week-cancel-button" className="rounded-full">Cancel</Button>
            <Button onClick={createWeek} data-testid="create-week-button" className="rounded-full bg-primary hover:bg-primary/90">Create week</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleting} onOpenChange={setDeleting}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove this week?</AlertDialogTitle>
            <AlertDialogDescription>All formats for {week ? fmtWeek(week.week_start) : "this week"} will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={removeWeek} data-testid="confirm-delete-week" className="rounded-full bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
